import React, { useMemo, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    ResponsiveContainer,
    Tooltip,
    CartesianGrid,
} from "recharts";
import { FiClock } from "react-icons/fi";
import "../../containers/SpAndSuAnalysis/style.css";

const daysOfWeek = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

export const transformPeakHoursData = (rawData) => {
    const transformedData = {};

    if (!rawData || !rawData?.weekdays) return transformedData;

    rawData.weekdays.forEach((dayData) => {
        const dayName = dayData.dayCode.toUpperCase(); // "MON", "TUE", etc.
        const hourlyData = dayData.hourlyData || [];

        transformedData[dayName] = hourlyData.map((hour) => ({
            time: hour.label, // "12am", "1am", ...
            popularity: Math.round(hour.count * 100), // convert to %
            isPeakHour: hour.isPeak, // boolean
        }));
    });

    return transformedData;
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const dataPoint = payload[0].payload;
        return (
            <div className="custom-tooltip">
                <p className="tooltip-time">
                    {dataPoint.isPeakHour ? `Peak Hour: ${dataPoint.time}` : label}
                </p>
                <p className="tooltip-bookings">
                    Bookings: {dataPoint.popularity}%
                </p>
            </div>
        );
    }
    return null;
};

export const CustomBar = (props) => {
    const { x, y, width, height, isPeakHour, payload } = props;
    const popularity = payload?.popularity;

    // New: choose color based on value
    let fill = "#06b6d4"; // default = cyan
    if (popularity === 0) fill = "#d1d5db"; // gray for zero
    else if (isPeakHour) fill = "#f59e0b"; // orange for peak

    // Keep a visible minimum height for gray bars (optional)
    const barHeight = popularity === 0 ? 4 : height;
    const barY = popularity === 0 ? y + (height - 4) : y;

    return (
        <rect
            x={x}
            y={barY}
            width={width}
            height={barHeight}
            fill={fill}
            rx={6}
            ry={6}
        />
    );
};


const PeakBookingHours = ({ data, title }) => {
    console.log(data)
    const [selectedDay, setSelectedDay] = useState("MON");
    const modifiedData = useMemo(() => transformPeakHoursData(data), [data]);
    const currentDayData = modifiedData[selectedDay] || [];

    const maxPopularity = Math.max(...currentDayData.map((i) => i?.popularity));
    const dataWithPeakInfo = currentDayData.map((item) => ({
        ...item,
        isPeakHour: item?.popularity === maxPopularity && maxPopularity > 0,
    }));

    return (
        <div className="popular-times-card">
            <div className="popular-chart-header">
                <FiClock className="chart-header-icon" />
                <h3 className="chart-header-title">{title}</h3>
            </div>

            <div className="day-tabs">
                {daysOfWeek.map((day) => (
                    <div
                        key={day}
                        className={`day-tab ${selectedDay === day ? "active" : ""}`}
                        onClick={() => setSelectedDay(day)}
                    >
                        {day}
                    </div>
                ))}
            </div>

            {
                dataWithPeakInfo && dataWithPeakInfo?.some((item) => item?.popularity > 0) ?
                    <>
                        <div className="chart-area">
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={dataWithPeakInfo}>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#e2e8f0"
                                        opacity={0.5}
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="time"
                                        stroke="#64748b"
                                        fontSize={12}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis hide />
                                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent" }} />
                                    <Bar
                                        dataKey="popularity"
                                        shape={<CustomBar />}
                                        barSize={20}
                                        radius={[8, 8, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="chart-legend">
                            <div className="legend-item">
                                <span className="legend-color peak"></span>
                                <span>Peak Hours</span>
                            </div>
                            <div className="legend-item">
                                <span className="legend-color normal"></span>
                                <span>Normal Hours</span>
                            </div>
                        </div>
                    </>
                    :
                    <p style={{ textAlign: 'center' }}>No Data Available</p>
            }
        </div>
    );
};

export default PeakBookingHours;
